import * as Pioneer from 'pioneer';
import { Cameras, SceneHelpers, Transitions, Placemarks } from 'pioneer-scripts';
import { setColorRectStyle } from '../components/color_bar';
import { getNearestDataIndex, setPrecipitationColorRectStyle } from '../components/color_bar_html';
import { setReadoutData } from '../components/data_readout';
import { formatNumber } from '../helpers/tools';
import { poiStore, gmapStore, datasetStore, datareadStore, kioskStore, refsStore, eventsStore } from './globalState';

import ar from '../languages/ar';
import en from '../languages/en';

class CameraManager {
	constructor(app, scene, pioneer) {
		this._scene = scene;
		this._app = app;
		this._pioneer = pioneer;

		/**
		 * @type {Pioneer.Entity}
		 * @private
		 */
		this._cameraEntity = this._scene.get('camera');

		/**
		 * The entities that are unselectable.
		 * @type {string[]}
		 * @private
		 */
		this._unselectableEntities = [];
		this._previousEntity = null;
		this._currentTarget = '';
		this._isCelsius = false;
		this._transitionComplete = false;

		// this.setIsCelcius = this.setIsCelcius.bind(this);
		// this.setupDataReadout = this.setupDataReadout.bind(this);
		//this.setupDataReadoutPoI = this.setupDataReadoutPoI.bind(this);
	}

	getCameraEntity() {
		return this._cameraEntity;
	}

	setIsCelcius(isCelsius) {
		this._isCelsius = isCelsius === 0;
	}

	/**
	 * Get Current Camera target.
	 * @returns {string}
	 */
	getCurrentTarget() {
		return this._currentTarget;
	}

	getTransitionComplete() {
		return this._transitionComplete;
	}

	setTransitionComplete(bool) {
		this._transitionComplete = bool;
	}

	/**
	 * Sets which entities are unselectable.
	 * @param {string[]} list
	 */
	setUnselectableEntities(list) {
		this._unselectableEntities = list;
	}

	/**
	 * Flies the camera to the entity with the given options, and then adds a select controller.
	 * @param {string} name
	 */
	async goToEntity(name, param) {
		const { get } = refsStore.stateSnapshot;
		// Check that the entity is not undefined
		if (name === undefined) {
			return;
		}

		// Check if already at the entity.
		const cameraEntity = this._scene.get('camera');

		this._currentTarget = name;

		// Turn off the always-loading of the previous entity.
		this._previousEntity = cameraEntity.getParent();
		if (this._previousEntity !== null) {
			const model = this._previousEntity.get('model');
			if (model !== null) {
				model.setForceLoaded(false);
			}
		}

		// Unset the forced midDistance setting of the camera.
		cameraEntity.getComponentByClass(Pioneer.CameraComponent).setMidDistance(undefined);


		// Start loading the entity's resources.
		const entity = this._scene.get(name);
		const model = entity.get('model');
		if (model !== null) {
			model.setForceLoaded(true);
			get('scene').addLoading(name, 'model');
			await model.getLoadedPromise();
			get('scene').removeLoading(name, 'model');
		}

		// Check if the target has changed while loading
		if (this._currentTarget !== name) {
			return;
		}

		// Use different goTo functions depending on the type of entity.
		try {
			if (name === 'earth') {
				await this._goToEarth(param);
			} else if (name.startsWith('sc_')) {
				await this._goToSpacecraft(name);
			}

			this._transitionComplete = true;
			// Manage loading of components
			get('scene').addLoading(name, 'camera');
			const promises = [];
			const components = ['spheroidLOD'];

			for (let i = 0; i < components.length; i++) {
				const component = this._scene.get(name, components[i]);
				if (component !== null) {
					promises.push(component.getLoadedPromise());
				}
			}

			Promise.all(promises).then(() => {
				get('scene').removeLoading(name, 'camera');
			});
		} catch (e) {
			if (e) {
				console.error(e);
				if (e?.message?.includes('Timed out')) {
					kioskStore.setGlobalState({ missionTimedOut: true });
				}
				this._transitionComplete = false;
			}
		}

		// Setup the pick controller for data reading.
		this._setupPickController();
		//this._setupPickControllerPoI();
	}

	/**
	 * Goes to the earth.
	 * @private
	 */
	async _goToEarth(param = {}) {
		const { latitude, longitude, time } = param;

		// Make sure the entity is ready.
		await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set(['earth', 'sun']));
		const cameraEntity = this._scene.get('camera');
		const earth = this._scene.get('earth');
		const sun = this._scene.get('sun');
		const cameraOrientation = new Pioneer.Quaternion();
		const dest = new Pioneer.Vector3();
		earth.getPositionRelativeToEntity(dest, Pioneer.Vector3.Zero, sun);
		dest.neg(dest);
		dest.normalize(dest);
		const planetUp = new Pioneer.Vector3();
		earth.getOrientation().getAxis(planetUp, 2);
		cameraOrientation.setFromAxes(undefined, dest, planetUp);
		let distance = Cameras.getDistanceToFitEntities(this._cameraEntity, cameraOrientation, earth, [earth]);
		const duration = cameraEntity.getParent() !== null ? 2.0 : 0.0;

		// Zoom camera out if 2k and hideUI
		const { hideUI } = this._app.state;
		const is2k = this._app.is2k();
		const is4k = this._app.is4k();
		distance *= (hideUI === true && is2k) || is4k ? 1.6 : 1.3; // Add some margin

		const position = new Pioneer.Vector3();
		if (latitude && longitude) {
			const latlon = new Pioneer.LatLonAlt(latitude, longitude, 0);
			SceneHelpers.llaToXYZ(position, earth, latlon, false);
		} else {
			cameraEntity.getPositionRelativeToEntity(position, Pioneer.Vector3.Zero, earth);
		}

		if (this._previousEntity !== null) {
			// Look at the entity.
			const destination = new Pioneer.Vector3();
			cameraEntity.getPositionRelativeToEntity(destination, Pioneer.Vector3.Zero, earth);

			await Cameras.goToEntity(cameraEntity, earth, {
				duration: duration / 6.0,
				up: true,
				distance: destination.magnitude()
			});
		}

		dest.mult(dest, distance);
		const options = {
			up: true,
			fixedToParent: true,
			destination: dest,
			transitionFunction: Transitions.jumpToLocationOnSphere.bind(undefined, 5, earth.getExtentsRadius(), true, earth)
		};

		if (time >= 0) {
			options.duration = time;
		}

		if (position.magnitude() > 0) {
			position.normalize(position);
			position.mult(position, distance);
			options.destination = position;
		}

		// check options here
		await Cameras.goToEntity(cameraEntity, earth, options);

		// Add zoom limits to the camera.
		const zoomController = this._scene.get('camera', 'zoom');
		if (zoomController instanceof Pioneer.ZoomController) {
			zoomController.setUseSpheroidRadiusForDistance(true);
			zoomController.setDistanceClamp(new Pioneer.Interval(100, earth.getExtentsRadius() * 50));
		}

		// Spin controller.
		const align = cameraEntity.getControllerByType('align');
		const spin = cameraEntity.addController('spin', undefined, align);
		if (spin instanceof Pioneer.SpinController) {
			spin.setAxis(Pioneer.Vector3.ZAxis, true);
			spin.setRate(0.01);
			spin.setUsingRealTime(true);
			spin.setRotatingPosition(true);
		}
		// Tap controller.
		const tap = cameraEntity.addController('tap');
		if (tap instanceof Pioneer.TapController) {
			tap.setTapCallback(() => {
				cameraEntity.removeController(spin);
				cameraEntity.removeController(tap);
			});
		}

		// Make the camera smoother when zoomed in.
		const orbitController = this._scene.get('camera', 'orbit');
		if (orbitController instanceof Pioneer.OrbitController) {
			orbitController.slowWhenCloseToParent(true);
		}
	}

	/**
	 * Goes to a spacecraft.
	 * @param {string} name
	 * @private
	 */
	async _goToSpacecraft(name) {
		//Merge questions: below is removed in current... 
    eventsStore.setGlobalState({ showOnGlobe: false });
		poiStore.setGlobalState({ showOnGlobePoI: false });
		gmapStore.setGlobalState({ showOnGlobeGmap: false });
		// Make sure the entity is ready.
		await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set([name, 'earth']));

		const entity = this._scene.get(name);
		const cameraEntity = this._scene.get('camera');
		let distance = entity.getExtentsRadius() * 3;
		const duration = cameraEntity.getParent() !== null ? 1.5 : 0.0;
		const earth = this._scene.get('earth');
		let orbiter = true;

		// Determine radii
		const minRadius = 1.2 * entity.getOcclusionRadius();
		let maxRadius = 10 * entity.getExtentsRadius();
		if (name === 'sc_iss_oco_3' || name === 'sc_iss_ecostress' || name === 'sc_iss_emit') {
			orbiter = false;
			maxRadius = 3 * entity.getParent().getExtentsRadius();
			distance *= 2;
		}

		distance = Pioneer.MathUtils.clamp(distance, minRadius, maxRadius);
		const destination = new Pioneer.Vector3();
		destination.mult(entity.getPosition(), distance * 20 / entity.getPosition().magnitude());
		// if there's a previous entity
		// and it's not the new entity we're at !(ISS -> ISS)
		if (this._previousEntity !== null && this._previousEntity.getName() !== name) {
			const fromEarth = this._previousEntity.getName() === 'earth';
			const isBetweenInstrAndSC = !(this._previousEntity !== entity.getParent() && this._previousEntity.getParent() !== entity);
			if (!fromEarth && !isBetweenInstrAndSC) {
				// SC to SC (and not SC to Instr or Instr to SC) then needs to go to Earth first
				await this._goToEarth();
			}
			if (!fromEarth && isBetweenInstrAndSC) {
				// Instr -> SC or SC -> Instr
			} else if (!fromEarth && !isBetweenInstrAndSC) {
				// SC -> SC
				// Do a jump around the earth to the entity.
				await Cameras.goToEntity(cameraEntity, entity, {
					destination,
					fixedToParent: true,
					up: false,
					duration,
					transitionFunction: Transitions.jumpToLocationOnSphere.bind(undefined, 5, earth.getExtentsRadius(), true, earth)
				});
			} else {
				// From Earth
				await Cameras.goToEntity(cameraEntity, entity, {
					destination,
					fixedToParent: true,
					up: false,
					duration,
					transitionFunction: Transitions.jumpToLocationOnSphere.bind(undefined, 5, this._previousEntity.getExtentsRadius(), true, entity)
				});
			}
		}

		// Position the camera to have a nice view of the entity.
		const destinationX = new Pioneer.Vector3();
		const destinationY = new Pioneer.Vector3();
		const destinationZ = new Pioneer.Vector3();
		destinationZ.normalize(entity.getPosition());
		destinationY.setNormalTo(destinationZ, entity.getVelocity());
		destinationY.neg(destinationY);
		destinationX.cross(destinationZ, destinationY);
		destination.addMult(destinationY, destinationZ, 0.66);
		destination.addMult(destination, destinationX, 0.66);
		destination.mult(destination, distance / destination.magnitude());
		destination.rotateInverse(entity.getOrientation(), destination);
		destinationZ.rotateInverse(entity.getOrientation(), destinationZ);

		if (name === 'sc_iss_emit') {
			// EMIT hole view - set magnitude of final destination
			destination.setMagnitude(Pioneer.Vector3.YAxisNeg, distance);
		}

		await Cameras.goToEntity(cameraEntity, entity, {
			duration,
			destination,
			distance,
			destinationUp: destinationZ,
			destinationInFocusFrame: true,
			orbiter,
			fixedToParent: true,
			up: true,
			zoom: true,
			transitionFunction: Transitions.jumpToLocationOnSphere.bind(undefined, 0, entity.getExtentsRadius(), false, entity)
		});

		// Zoom clamping
		cameraEntity.get('zoom').setDistanceClamp(new Pioneer.Interval(minRadius, maxRadius));

		cameraEntity.get('orbit').setPitchAngleLimits(new Pioneer.Interval(-Math.PI / 2 + 0.01, Math.PI / 2 - 0.01));
	}


	/**
	 * Goes to a video patch.
	 * @param {any} video
	 */
	async goToVideoPatch(videoId, video) {
		const earth = this._scene.get('earth');
		let placemarkEntity = this._scene.get('event_video_' + videoId);
		const lat = (video.south_boundary + video.north_boundary) / 2;
		const lon = (video.west_boundary + video.east_boundary) / 2;
		if (!placemarkEntity) {
			placemarkEntity = Placemarks.addPlacemark('event_video_' + videoId, '', earth, lat, lon, 0, true);
		}
		await this.goToDataPatch(video.north_boundary, video.south_boundary, video.east_boundary, video.west_boundary, placemarkEntity);
	}

	/**
	 * Goes to a video patch.
	 * @param {any} video
	 */
	 async goToVideoPatchPoI(videoIdPoI, videoPoI) {
		const earth = this._scene.get('earth');
		let placemarkEntityPoI = this._scene.get('poi_video_' + videoIdPoI);
		const lat = (videoPoI.south_boundary + videoPoI.north_boundary) / 2;
		const lon = (videoPoI.west_boundary + videoPoI.east_boundary) / 2;
		if (!placemarkEntityPoI) {
			placemarkEntityPoI = Placemarks.addPlacemark('poi_video_' + videoIdPoI, '', earth, lat, lon, 0, true);
		}
		await this.goToDataPatch(videoPoI.north_boundary, videoPoI.south_boundary, videoPoI.east_boundary, videoPoI.west_boundary, placemarkEntityPoI);
	}

	/**
	 * Goes to a video patch.
	 * @param {any} video
	 */
	 async goToVideoPatchGmap(videoIdGmap, videoGmap) {
		const earth = this._scene.get('earth');
		let placemarkEntityGmap = this._scene.get('gmap_video_' + videoIdGmap);
		const lat = (videoGmap.south_boundary + videoGmap.north_boundary) / 2;
		const lon = (videoGmap.west_boundary + videoGmap.east_boundary) / 2;
		if (!placemarkEntityGmap) {
			placemarkEntityGmap = Placemarks.addPlacemark('gmap_video_' + videoIdGmap, '', earth, lat, lon, 0, true);
		}
		await this.goToDataPatch(videoGmap.north_boundary, videoGmap.south_boundary, videoGmap.east_boundary, videoGmap.west_boundary, placemarkEntityGmap);
	}


	/**
	 * Goes to a data patch.
	 * @param {any} event
	 */
	async goToEventPatch(event) {
		const earth = this._scene.get('earth');
		let placemarkEntity = this._scene.get('event_' + event.id);
		if (!placemarkEntity) {
			const lat = Number(event.center.split(',')[0]);
			const lon = Number(event.center.split(',')[1]);
			placemarkEntity = Placemarks.addPlacemark('event_' + event.id, 'foooooo', earth, lat, lon, 0, true);
		}
		await this.goToDataPatch(event.north_boundary, event.south_boundary, event.east_boundary, event.west_boundary, placemarkEntity);
	}

	/**
	 * Goes to a data patch.
	 * @param {any} poi
	 */
	 async goToPoIPatch(poi) {
		const earth = this._scene.get('earth');
		let placemarkEntityPoI = this._scene.get('poi_' + poi.id);
		if (!placemarkEntityPoI) {
			const lat = Number(poi.center.split(',')[0]);
			const lon = Number(poi.center.split(',')[1]);
			placemarkEntityPoI = Placemarks.addPlacemark('poi_' + poi.id, '', earth, lat, lon, 0, true);
		}

		//To check if there is a Key named "distance_from_poi" a key added to poi.json file
		// camera zoom
		//  let distanceFromPoiFixed = 0;
		//  if(poi.hasOwnProperty("distance_from_poi"))
		//  {
		const distanceFromPoiFixed = poi.distance_from_poi;
		//  }
		 //console.log("distanceFromPoiFixed: ",distanceFromPoiFixed);
		await this.goToDataPatchPoI(poi.north_boundary, poi.south_boundary, poi.east_boundary, poi.west_boundary, placemarkEntityPoI, distanceFromPoiFixed, poi.zoomFactor);
	}

	/**
	 * Goes to a data patch.
	 * @param {any} gmap
	 */
	 async goToGmapPatch(gmap) {
		const earth = this._scene.get('earth');
		let placemarkEntityGmap = this._scene.get('gmap_' + gmap.id);
		if (!placemarkEntityGmap) {
			const lat = Number(gmap.center.split(',')[0]);
			const lon = Number(gmap.center.split(',')[1]);
			placemarkEntityGmap = Placemarks.addPlacemark('gmap_' + gmap.id, '', earth, lat, lon, 0, true);
		}

		const distanceFromGmapFixed = gmap.distance_from_gmap;
		 //console.log("distanceFromGmapFixed: ",distanceFromGmapFixed);
		await this.goToDataPatchGmap(gmap.north_boundary, gmap.south_boundary, gmap.east_boundary, gmap.west_boundary, placemarkEntityGmap, distanceFromGmapFixed, gmap.zoomFactor);
	}

	/**
	 * Goes to a data patch either event or video.
	 * @param {*} northBoundary
	 * @param {*} southBoundary
	 * @param {*} patchEntity
	 */
	async goToDataPatch(northBoundary, southBoundary, eastBoundary, westBoundary, placemarkEntity) {
		placemarkEntity.setEnabled(true);

		// Disabling the element is hacky and should be fixed with better state management / element organization.
		placemarkEntity.get('div').setEnabled(false);
	
		const camera = this._scene.get('camera', 'camera');
		if (!(camera instanceof Pioneer.CameraComponent)) {
			throw new Error();
		}

		const cameraEntity = this._scene.get('camera');
		const earth = this._scene.get('earth');
		earth.get('atmosphere').setEnabled(false);

		// set the mid distance for mobile powerscaling
		camera.setMidDistance(120000);

		if (cameraEntity.getParent() !== null) {
			// Enable the eventPlacemark so the camera can transtion
			cameraEntity.getParent().setEnabled(true);
		}

		// Get some good lat and lon distances so that event looks good on the screen.
		const latAngularDistance = Pioneer.MathUtils.degToRad(northBoundary - southBoundary);
		const lonAngularDistance = Pioneer.MathUtils.degToRad(eastBoundary - westBoundary + (eastBoundary < westBoundary ? 360 : 0));
		const latFOVFactor = 1.0 / Math.atan(this._scene.get('camera', 'camera').getVerticalFieldOfView() / 2.0);
		const lonFOVFactor = 1.0 / Math.atan(this._scene.get('camera', 'camera').getHorizontalFieldOfView() / 2.0);
		const latDistance = latAngularDistance * earth.getExtentsRadius() * latFOVFactor;
		const lonDistance = lonAngularDistance * earth.getExtentsRadius() * lonFOVFactor;
		const distanceFromEvent = Math.sqrt(latDistance * latDistance + lonDistance * lonDistance) * 0.5;
		
		// Wait until the placemark and the earth has a valid position and orientation.
		await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set([placemarkEntity.getName(), 'earth']));

		// Position camera destination to be above the event.
		const eventPosition = placemarkEntity.getPosition();
		const destination = new Pioneer.Vector3();
		destination.normalize(eventPosition);
		destination.mult(destination, distanceFromEvent);
		await Cameras.goToEntity(cameraEntity, placemarkEntity, {
			destination: destination,
			fixedToParent: true,
			up: false,
			duration: 3,
			transitionFunction: Transitions.jumpToLocationOnSphere.bind(0, 5, earth.getExtentsRadius(), true, earth)
		});
		// Make the camera align to the placemark's north-east orientation.
		const alignController = this._scene.get('camera', 'align');
		if (!(alignController instanceof Pioneer.AlignController)) {
			throw new Error();
		}
		alignController.setSecondaryAlignType('align');
		alignController.setSecondaryAxis(Pioneer.Vector3.ZAxis);
		alignController.setSecondaryTargetEntity(placemarkEntity.getName());
		alignController.setSecondaryTargetAxis(Pioneer.Vector3.YAxis);
		// Make the camera orbit the placemark with nice limits on its axes.
		const orbitController = this._scene.get('camera', 'orbit');
		if (!(orbitController instanceof Pioneer.OrbitController)) {
			throw new Error();
		}
		orbitController.setYawAxisType('y-axis');
		orbitController.setPitchAxisType('z-axis');
		orbitController.setYawAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
		orbitController.setPitchAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
		// Add zoom limits to the camera.
		const zoomController = this._scene.get('camera', 'zoom');
		if (!(zoomController instanceof Pioneer.ZoomController)) {
			throw new Error();
		}
		zoomController.setDistanceClamp(new Pioneer.Interval(20, earth.getExtentsRadius() * 3));
	}

  /**
	 * Goes to a data patch either poi or video.
	 * @param {*} northBoundary
	 * @param {*} southBoundary
	 * @param {*} patchEntity
	 */
	 async goToDataPatchPoI(northBoundary, southBoundary, eastBoundary, westBoundary, placemarkEntityPoI,distanceFromPoiFixed,zoomFactorPoi) {
		placemarkEntityPoI.setEnabled(true);
		// Disabling the element is hacky and should be fixed with better state management / element organization.
		placemarkEntityPoI.get('div').setEnabled(false);
		const camera = this._scene.get('camera', 'camera');
		if (!(camera instanceof Pioneer.CameraComponent)) {
			throw new Error();
		}

		const cameraEntity = this._scene.get('camera');
		const earth = this._scene.get('earth');
		earth.get('atmosphere').setEnabled(false);

		// set the mid distance for mobile powerscaling
		camera.setMidDistance(120000);
		// Get some good lat and lon distances so that event looks good on the screen.
		const latAngularDistance = Pioneer.MathUtils.degToRad(northBoundary - southBoundary);
		const lonAngularDistance = Pioneer.MathUtils.degToRad(eastBoundary - westBoundary + (eastBoundary < westBoundary ? 360 : 0));
		
		const latFOVFactor = zoomFactorPoi / Math.atan(this._scene.get('camera', 'camera').getVerticalFieldOfView() / 2.0);
		const lonFOVFactor = zoomFactorPoi / Math.atan(this._scene.get('camera', 'camera').getHorizontalFieldOfView() / 2.0);
	   
		const latDistance = latAngularDistance * earth.getExtentsRadius() * latFOVFactor;
		const lonDistance = lonAngularDistance * earth.getExtentsRadius() * lonFOVFactor;
		const distanceFromPoI = Math.sqrt(latDistance * latDistance + lonDistance * lonDistance) * 0.5;
		// Wait until the placemark and the earth has a valid position and orientation.
		await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set([placemarkEntityPoI.getName(), 'earth']));
		// Position camera destination to be above the poi.
		const poiPosition = placemarkEntityPoI.getPosition();
		const destination = new Pioneer.Vector3();
		destination.normalize(poiPosition);
		destination.mult(destination, distanceFromPoI);
		await Cameras.goToEntity(cameraEntity, placemarkEntityPoI, {
			destination: destination,
			fixedToParent: true,
			up: false,
			duration: 3,
			transitionFunction: Transitions.jumpToLocationOnSphere.bind(0, 5, earth.getExtentsRadius(), true, earth)
		});
		// Make the camera align to the placemark's north-east orientation.
		const alignController = this._scene.get('camera', 'align');
		if (!(alignController instanceof Pioneer.AlignController)) {
			throw new Error();
		}
		alignController.setSecondaryAlignType('align');
		alignController.setSecondaryAxis(Pioneer.Vector3.ZAxis);
		alignController.setSecondaryTargetEntity(placemarkEntityPoI);
		alignController.setSecondaryTargetAxis(Pioneer.Vector3.YAxis);
		// Make the camera orbit the placemark with nice limits on its axes.
		const orbitController = this._scene.get('camera', 'orbit');
		if (!(orbitController instanceof Pioneer.OrbitController)) {
			throw new Error();
		}
		orbitController.setYawAxisType('y-axis');
		orbitController.setPitchAxisType('z-axis');
		orbitController.setYawAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
		orbitController.setPitchAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
		// Add zoom limits to the camera.
		const zoomController = this._scene.get('camera', 'zoom');
		if (!(zoomController instanceof Pioneer.ZoomController)) {
			throw new Error();
		}

		//This control Zoom in level of POIs 
		// if(distanceFromPoiFixed > 0){
			//console.log("distanceFromPoIFixed: ",distanceFromPoiFixed);
			zoomController.setDistanceClamp(new Pioneer.Interval(distanceFromPoiFixed, earth.getExtentsRadius() * 3));
		// } else {
		//console.log("distanceFromPoI: ",distanceFromPoI);
		// 	zoomController.setDistanceClamp(new Pioneer.Interval(distanceFromPoI, earth.getExtentsRadius() * 3));
		// }
		//zoomController.setDistanceClamp(new Pioneer.Interval(20, earth.getExtentsRadius() * 3));
	}

	 /**
	 * Goes to a data patch either gmap or video.
	 * @param {*} northBoundary
	 * @param {*} southBoundary
	 * @param {*} patchEntity
	 */
	  async goToDataPatchGmap(northBoundary, southBoundary, eastBoundary, westBoundary, placemarkEntityGmap,distanceFromGmapFixed,zoomFactorGmap) {
		placemarkEntityGmap.setEnabled(true);
		// Disabling the element is hacky and should be fixed with better state management / element organization.
		placemarkEntityGmap.get('div').setEnabled(false);
		const camera = this._scene.get('camera', 'camera');
		if (!(camera instanceof Pioneer.CameraComponent)) {
			throw new Error();
		}

		const cameraEntity = this._scene.get('camera');
		const earth = this._scene.get('earth');
		earth.get('atmosphere').setEnabled(false);

		// set the mid distance for mobile powerscaling
		camera.setMidDistance(120000);
		// Get some good lat and lon distances so that event looks good on the screen.
		const latAngularDistance = Pioneer.MathUtils.degToRad(northBoundary - southBoundary);
		const lonAngularDistance = Pioneer.MathUtils.degToRad(eastBoundary - westBoundary + (eastBoundary < westBoundary ? 360 : 0));
		
		const latFOVFactor = zoomFactorGmap / Math.atan(this._scene.get('camera', 'camera').getVerticalFieldOfView() / 2.0);
		const lonFOVFactor = zoomFactorGmap / Math.atan(this._scene.get('camera', 'camera').getHorizontalFieldOfView() / 2.0);
	   
		const latDistance = latAngularDistance * earth.getExtentsRadius() * latFOVFactor;
		const lonDistance = lonAngularDistance * earth.getExtentsRadius() * lonFOVFactor;
		const distanceFromGmap = Math.sqrt(latDistance * latDistance + lonDistance * lonDistance) * 0.5;
		// Wait until the placemark and the earth has a valid position and orientation.
		await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set([placemarkEntityGmap.getName(), 'earth']));
		// Position camera destination to be above the gmap.
		const gmapPosition = placemarkEntityGmap.getPosition();
		const destination = new Pioneer.Vector3();
		destination.normalize(gmapPosition);
		destination.mult(destination, distanceFromGmap);
		await Cameras.goToEntity(cameraEntity, placemarkEntityGmap, {
			destination: destination,
			fixedToParent: true,
			up: false,
			duration: 3,
			transitionFunction: Transitions.jumpToLocationOnSphere.bind(0, 5, earth.getExtentsRadius(), true, earth)
		});
		// Make the camera align to the placemark's north-east orientation.
		const alignController = this._scene.get('camera', 'align');
		if (!(alignController instanceof Pioneer.AlignController)) {
			throw new Error();
		}
		alignController.setSecondaryAlignType('align');
		alignController.setSecondaryAxis(Pioneer.Vector3.ZAxis);
		alignController.setSecondaryTargetEntity(placemarkEntityGmap);
		alignController.setSecondaryTargetAxis(Pioneer.Vector3.YAxis);
		// Make the camera orbit the placemark with nice limits on its axes.
		const orbitController = this._scene.get('camera', 'orbit');
		if (!(orbitController instanceof Pioneer.OrbitController)) {
			throw new Error();
		}
		orbitController.setYawAxisType('y-axis');
		orbitController.setPitchAxisType('z-axis');
		orbitController.setYawAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
		orbitController.setPitchAngleLimits(new Pioneer.Interval(-Math.PI / 4, Number(Math.PI) / 4));
		// Add zoom limits to the camera.
		const zoomController = this._scene.get('camera', 'zoom');
		if (!(zoomController instanceof Pioneer.ZoomController)) {
			throw new Error();
		}

		
			zoomController.setDistanceClamp(new Pioneer.Interval(distanceFromGmapFixed, earth.getExtentsRadius() * 3));
	}

	/**
	 * Offsets Pioneer Viewport Left by percent.
	 * Used to offset globe during desktop charts and modal videos.
	 * Additionally it offsets the dateString and colorbar elements to match the globe.
	 * @param {number} percent
	 */
		offsetPioneerViewportLeft(percent) {
		const mainViewportEl = this._app.pioneer?.getViewport(0).getDiv();

		const isMobile = this._app.isMobile();

		let percentString = percent.toString();
		if (percent !== 0) {
			percentString += '%';
		}
		const translate = 50 + percent;

		// Handle the case for smaller tablets of landscape width between 1000px and 1100 px (iPad mini: 1024x768. not iPad air: 1180x820).
		// This helps prevent the title of a vital sign overlap with the colorbar on iPad mini when viewing a modal video or chart in landscape.
		if (window.matchMedia('(orientation: landscape)').matches && (window.matchMedia('(min-width: 1100px)').matches || window.matchMedia('(max-width: 1000px').matches)) {
			const legendContainerDiv = document.getElementById('legend-container');
			legendContainerDiv && (legendContainerDiv.style.right = percentString);
		}

		const dateRangeContainerDiv = document.getElementById('date-label-container');
		// Mobile layouts do not have the dateRangeContainer shifted to 50%.
    //this one at the very least has to go. doesn't work for rtl if it works anywhere ever...
		// (dateRangeContainerDiv && !isMobile) && (dateRangeContainerDiv.style.transform = `translate(-${translate}%)`);

		if (mainViewportEl) {
			mainViewportEl.style.right = percentString;
		}
	}
	/**
	 * Adds a select controller.
	 * @private
	 */
	_setupPickController() {
		const cameraEntity = this._scene.get('camera');
		const hoverController = cameraEntity.addController('pick');
		const earth = this._scene.get('earth');
		hoverController.setPickedEntity(earth);
		hoverController.setTriggerOnHover(false);

		hoverController.setCallback(position => this._calcDataFromPosition(position));
	}

	/**
	 * - Inputs an xy position vector,
	 * - Applies latlong conversions to allow it to sample the value from the same spot on the 2d canvas map
	 * - Uses readoutData values to calculate data values and thumb percentage
	 * - Sets them if valid
	 * @param {Vector2} position
	 */
	_calcDataFromPosition(position) {
		const { get } = refsStore.stateSnapshot;
		const { hasAnimation, isPaused } = datasetStore.stateSnapshot;
		const { isCelcius } = datareadStore.stateSnapshot;
		const { readoutData, dataset } = get('dataset');

		const allowSample = (!hasAnimation || isPaused) && readoutData && (position?.x && position.y);

		// Return if sample not allowed.
		if (!allowSample) {
			return;
		}

		const earth = this._scene.get('earth');
		const soilMoistAndSal = dataset.externalId === 'smapSmSalinity8Day';
		const precipitation = dataset.externalId === 'precipitationToday';

		// Format latitude and longitude
		const xy = Pioneer.Vector2.pool.get();
		console.log('x, y', xy)
		const lla = Pioneer.LatLonAlt.pool.get();
		console.log('lla', lla)
		const positionInFrame = Pioneer.Vector3.pool.get();
		console.log('positionInFrame', positionInFrame)
		positionInFrame.rotateInverse(earth.getOrientation(), position);
		earth.get('spheroid').llaFromXYZ(lla, positionInFrame);

		xy.x = (Pioneer.MathUtils.radToDeg(lla.lon) + 180.0) / 360 * 1440;
		console.log('x',xy.x )

		console.log('lla.lon', Pioneer.MathUtils.radToDeg(lla.lon), lla.lon)
		xy.y = (1 - (Pioneer.MathUtils.radToDeg(lla.lat) + 90.0) / 180) * 720;
		console.log('y',xy.y )
		console.log('lla.lat', Pioneer.MathUtils.radToDeg(lla.lat), lla.lat)

		const [dataR = 1, dataG, dataB] = get('scene').getValueFromCanvas(xy.x, xy.y) || [];
		const noData = dataR === 1;


		// If the first data item is 1, we've sampled a black area. Release the vector and return early.
		if (noData) {
			Pioneer.LatLonAlt.pool.release(lla);
			Pioneer.Vector2.pool.release(xy);
			Pioneer.Vector3.pool.release(positionInFrame);
			return;
		}

		// Unique case: If precipitation data, apply individual calculation and rect style.
		if (precipitation) {
			const { rain, snow } = readoutData;
			const { scalingFactor: rainScale, units: rainUnits } = rain;
			const { scalingFactor: snowScale, units: snowUnits } = snow;


			// Specific precipitation equations contact: Jeff Hall.
			const readValue = dataG + 256 * dataB;
			const isSnow = dataR === 2;

			const scale = isSnow ? snowScale : rainScale;
			const units = isSnow ? snowUnits : rainUnits;

			const dataValue = readValue * scale;

			// Format the value with the unitss for setReadoutData
			const valueString = `${formatNumber(dataValue, 2)} ${units}`;

			// add latlon values for every vital sign value -zahrah
			let latlonx = Pioneer.MathUtils.radToDeg(lla.lat)
			let latlony = Pioneer.MathUtils.radToDeg(lla.lon)
			let decimalPlace = 2
			const valueLatLonStringx = `${Math.abs(latlonx).toFixed(decimalPlace)} ° ${latlonx > 0 ? 'N' : 'S'}`;
			const valueLatLonStringy = `${Math.abs(latlony).toFixed(decimalPlace)} ° ${latlony > 0 ? 'E' : 'W'}`;
			const valueLatLonText = "" + valueLatLonStringx + "" + ", " + valueLatLonStringy

			// add latlon values readout -zahrah
			setReadoutData(valueString, valueLatLonText, this._app.getCursorPosition());
			console.log("readout", valueString, valueLatLonText, this._app.getCursorPosition())
			
			// Get the nearest dataLookup index and pass to setPrecipitationColorRectStyle.
			const nearestIndex = getNearestDataIndex(dataValue);
			setPrecipitationColorRectStyle(nearestIndex, isSnow, this._pioneer);

			Pioneer.LatLonAlt.pool.release(lla);
			Pioneer.Vector2.pool.release(xy);
			Pioneer.Vector3.pool.release(positionInFrame);

			return;
		}

		// Get readout data vars.
		const { bottom, variation, numOffset, scalingFactor, units, dps } = readoutData;

		// Explorer equation - value = (int)(color.g * 255 * 255) + (int)(color.b * 255)) * vD.scalingFactor + vD.numOffset

		// Declare vars.
		let readValue = ((dataB + (256 * dataG)) * scalingFactor) + numOffset;
		let percentScale = null;
		let dataUnits = units;
		let decimalPlaces = dps ?? 1;
		let isDoubleColorBar = false;
		let isUpper = true;

		// Calculate % value before celsius conversion.
		if (!isNaN(readValue)) {
			percentScale = Pioneer.MathUtils.clamp((readValue - bottom) / (variation), 0, 1); // Values can exceed colorbar scale (GFO, SSHA, etc.)
		}

		if (soilMoistAndSal) {
			if (readValue >= 1001) { // NaN returns false
				// WATER - salinity
				dataUnits = 'PSU'; // is there somewhere that explains this?
				readValue *= 0.01;
				decimalPlaces = 1;
				percentScale = Pioneer.MathUtils.clamp((readValue - 30) / 10, 0, 1); // Range of 30-40 PSU
				isUpper = false;
			} else {
				// Land cm3/cm3
				readValue *= 0.001;
				percentScale = Pioneer.MathUtils.clamp((readValue - 0) / 0.6, 0, 1); // Range of 0.0 - 0.6 cm3/cm3
				isUpper = true;
			}

			// Trigger offsets for double color bar
			isDoubleColorBar = true;
		} else if (dataUnits === 'ف°' && isCelcius) {
			// Convert to Celsius to match color bar.
			//units = '°C';   // to change to arabic celsius
			dataUnits = 'م°';
			// Celsius = (Fahrenheit - 32) / 1.8
			readValue = (readValue - 32) / 1.8;
		}

		// Validation.
		if (percentScale !== null && !isNaN(percentScale) && readValue !== null && !isNaN(readValue)) {
			const valueString = `${formatNumber(readValue, decimalPlaces)} ${dataUnits}`;

			// add latlon values for every vital sign value -zahrah
			let latlonx = Pioneer.MathUtils.radToDeg(lla.lat)
			let latlony = Pioneer.MathUtils.radToDeg(lla.lon)
			let decimalPlace = 2
			const valueLatLonStringx = `${Math.abs(latlonx).toFixed(decimalPlace)} ° ${latlonx > 0 ? 'N' : 'S'}`;
			const valueLatLonStringy = `${Math.abs(latlony).toFixed(decimalPlace)} ° ${latlony > 0 ? 'E' : 'W'}`;
			const valueLatLonText = "" + valueLatLonStringx + "" + ", " + valueLatLonStringy
			// Set colorbar value position and readout reading.
      // subtract from 1 to invert for rtl colorbar !! super important !!
			setColorRectStyle(1 - percentScale, isDoubleColorBar, isUpper);
			// add latlon values readout -zahrah
			setReadoutData(valueString, valueLatLonText, this._app.getCursorPosition());
			console.log("readout", valueString, valueLatLonText, this._app.getCursorPosition())
		}

		Pioneer.LatLonAlt.pool.release(lla);
		Pioneer.Vector2.pool.release(xy);
		Pioneer.Vector3.pool.release(positionInFrame);
	}
}

export default CameraManager;
